import { FC, useEffect, useMemo, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import dayjs from 'dayjs';

type DateFilterProps = {
  selectedDate: string;
  onDateChange: (date: string) => void;
};

const DateFilter: FC<DateFilterProps> = ({ selectedDate, onDateChange }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const today = dayjs().format('YYYY-MM-DD');

  const dateOptions = useMemo(() => {
    const dates = [];
    const todayDate = dayjs();

    for (let i = -5; i < 8; i++) {
      const date = todayDate.add(i, 'day');
      dates.push({
        date: date.format('YYYY-MM-DD'),
        display: date.format('ddd M/D'),
        dayName: date.format('ddd').toUpperCase(),
        dayNumber: date.format('M/D'),
      });
    }

    return dates;
  }, []);

  const calculateScrollPosition = (targetIndex: number) => {
    if (!scrollContainerRef.current) return 0;

    const container = scrollContainerRef.current;
    const items = container.querySelectorAll('[data-date-item]');

    if (items.length === 0 || targetIndex >= items.length) return 0;

    const targetItem = items[targetIndex] as HTMLElement;
    const targetItemWidth = targetItem.offsetWidth;

    const targetItemLeft = targetItem.offsetLeft;

    const scrollPosition = targetItemLeft - (container.clientWidth - targetItemWidth) / 2;

    return Math.max(0, scrollPosition);
  };

  useEffect(() => {
    const todayIndex = dateOptions.findIndex((option) => option.date === today);

    if (todayIndex !== -1 && scrollContainerRef.current) {
      const scrollPosition = calculateScrollPosition(todayIndex);

      scrollContainerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }
  }, [dateOptions, today]);

  const centerDate = (date: string) => {
    const dateIndex = dateOptions.findIndex((option) => option.date === date);

    if (dateIndex !== -1 && scrollContainerRef.current) {
      const scrollPosition = calculateScrollPosition(dateIndex);

      scrollContainerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleDateClick = (date: string) => {
    onDateChange(date);
    centerDate(date);
  };

  return (
    <Box position="relative" p="32px 0 12px" width="100vw" maxW="500px" overflow="hidden">
      <Box
        position="absolute"
        left="0"
        top="0"
        bottom="0"
        width="40px"
        background="linear-gradient(to right, rgba(36,36,36,0.8) 0%, rgba(36,36,36,0.4) 50%, transparent 100%)"
        zIndex="2"
        pointerEvents="none"
      />

      <Box
        position="absolute"
        right="0"
        top="0"
        bottom="0"
        width="40px"
        background="linear-gradient(to left, rgba(36,36,36,0.8) 0%, rgba(36,36,36,0.4) 50%, transparent 100%)"
        zIndex="2"
        pointerEvents="none"
      />

      <Box
        ref={scrollContainerRef}
        overflowX="auto"
        overflowY="hidden"
        px="20px"
        width="100%"
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <Flex gap="0" alignItems="center" width="max-content" minW="max-content">
          {dateOptions.map(({ date, display, dayName, dayNumber }) => (
            <Box
              key={date}
              data-date-item
              cursor="pointer"
              px="10px"
              onClick={() => handleDateClick(date)}
              textAlign="center"
            >
              <Flex
                alignItems="center"
                justifyContent="center"
                gap="4px"
                flexDirection="row"
                fontFamily="Special Gothic Expanded One"
              >
                <Box fontSize="12px" color={selectedDate === date ? '#F83C3C' : 'rgba(255, 255, 255, 1)'}>
                  {dayName}
                </Box>
                <Box fontSize="12px" color={selectedDate === date ? '#F83C3C' : 'white'}>
                  {dayNumber}
                </Box>
              </Flex>
            </Box>
          ))}
        </Flex>
      </Box>
    </Box>
  );
};

export default DateFilter;
